import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LayoutDashboard, Loader2, Save, ExternalLink } from "lucide-react";
import { DashShell } from "@/components/dash/DashShell";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const NAV = [{ to: "/account/brand", label: "My Brand", icon: LayoutDashboard }];

export default function BrandProfileEdit() {
    const [f, setF] = useState(null);
    const [slug, setSlug] = useState(null);
    const [products, setProducts] = useState([]);
    const [updates, setUpdates] = useState([]);
    const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

    useEffect(() => {
        api.get("/brand/me").then(({ data }) => {
            setF(data || {}); if (data) { setSlug(data.slug); setProducts(data.products || []); setUpdates(data.updates || []); }
        });
    }, []);

    const save = async () => {
        if (!f.name) { toast.error("Brand name is required"); return; }
        try {
            const { data } = await api.post("/brand/profile", { ...f, products, updates, media: f.media || [] });
            setSlug(data.slug); toast.success("Brand profile saved");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    if (f === null) return <DashShell nav={NAV} title="My Brand"><div className="grid place-items-center py-32 text-teal-900/40"><Loader2 className="w-7 h-7 animate-spin" /></div></DashShell>;

    return (
        <DashShell nav={NAV} title="My Brand Profile">
            <div className="max-w-2xl space-y-5">
                {slug && <Link to={`/brand/${slug}`} className="inline-flex items-center gap-1.5 text-sm text-teal-700 hover:underline" data-testid="view-public-brand">View public profile <ExternalLink className="w-3.5 h-3.5" /></Link>}
                <div className="bg-white rounded-2xl border border-black/5 p-6 shadow-sm space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Fld label="Brand Name *"><Input value={f.name || ""} onChange={set("name")} data-testid="brand-name" /></Fld>
                        <Fld label="Industry"><Input value={f.industry || ""} onChange={set("industry")} data-testid="brand-industry" /></Fld>
                        <Fld label="Location"><Input value={f.location || ""} onChange={set("location")} data-testid="brand-location" /></Fld>
                        <Fld label="Website"><Input value={f.website || ""} onChange={set("website")} data-testid="brand-website" /></Fld>
                        <Fld label="Instagram"><Input value={f.instagram || ""} onChange={set("instagram")} /></Fld>
                        <Fld label="Logo / Cover Image URL"><Input value={f.image || ""} onChange={set("image")} data-testid="brand-image" /></Fld>
                    </div>
                    <Fld label="About"><Textarea rows={3} value={f.about || ""} onChange={set("about")} data-testid="brand-about" /></Fld>
                    <ListEditor label="Products / Services" items={products} setItems={setProducts} fields={["name", "detail"]} testid="brand-products" />
                    <ListEditor label="Updates" items={updates} setItems={setUpdates} fields={["text"]} testid="brand-updates" />
                    <button onClick={save} className="btn-gold rounded-full px-6 py-2.5 text-sm flex items-center gap-2" data-testid="brand-save"><Save className="w-4 h-4" /> Save Profile</button>
                </div>
            </div>
        </DashShell>
    );
}

function ListEditor({ label, items, setItems, fields, testid }) {
    const add = () => setItems([...items, Object.fromEntries(fields.map((f) => [f, ""]))]);
    const upd = (i, k, v) => setItems(items.map((x, j) => j === i ? { ...x, [k]: v } : x));
    return (
        <div>
            <div className="flex justify-between items-center mb-2"><Label className="text-sm">{label}</Label><button onClick={add} className="text-xs text-teal-700 hover:underline" data-testid={`${testid}-add`}>+ Add</button></div>
            <div className="space-y-2">
                {items.map((it, i) => (
                    <div key={i} className="grid gap-2" style={{ gridTemplateColumns: `repeat(${fields.length}, 1fr)` }}>
                        {fields.map((fl) => <Input key={fl} placeholder={fl} value={it[fl] || ""} onChange={(e) => upd(i, fl, e.target.value)} />)}
                    </div>
                ))}
                {items.length === 0 && <p className="text-xs text-teal-900/40">None added yet.</p>}
            </div>
        </div>
    );
}
function Fld({ label, children }) { return <div className="space-y-1.5"><Label className="text-sm">{label}</Label>{children}</div>; }
